// frontend/src/api.js  –  v3 (dynamic BASE URL + PAT support)

// Server URL: custom (fra Settings) → build-time env → '' (Vite dev proxy)
// Gemmes i localStorage så det er synkront tilgængeligt uden async-init
function getBase() {
  const custom = localStorage.getItem('freshtrack_server_url');
  if (custom) return custom.replace(/\/+$/, '');
  // VITE_API_URL sættes til https://kitchey.aihuset.dk i .env.production
  return import.meta.env.VITE_API_URL || '';
}

export function getServerUrl() { return getBase() || window.location.origin; }

// Byg absolut URL til uploadede billeder. I bundlede Capacitor-builds er origin
// capacitor://localhost / localhost, så relative /uploads-stier 404'er — derfor
// præfikses med backend-basen (tom i web-dev → relativ via Vite-proxy).
export const uploadUrl = (filename) => `${getBase()}/uploads/${filename}`;
export function setServerUrl(url) {
  if (url) localStorage.setItem('freshtrack_server_url', url.replace(/\/+$/, ''));
  else localStorage.removeItem('freshtrack_server_url');
}

// ── Token-håndtering ────────────────────────────────────────────────────
function getAccess()  { return localStorage.getItem('access_token') || ''; }
function getRefresh() { return localStorage.getItem('refresh_token') || ''; }
function saveTokens({ access_token, refresh_token }) {
  localStorage.setItem('access_token', access_token);
  if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
}
function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('active_household');
}

function getActiveHousehold() {
  return localStorage.getItem('active_household') || '';
}
function setActiveHousehold(id) {
  localStorage.setItem('active_household', id);
}

let refreshPromise = null;

async function req(method, path, body, opts = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccess();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const hhId = getActiveHousehold();
  if (hhId && !opts.skipHousehold) headers['X-Household-Id'] = hhId;

  let res;
  try {
    res = await fetch(`${getBase()}/api${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    window.dispatchEvent(new CustomEvent('kitchey:network-error'));
    throw new Error('NETWORK_ERROR');
  }

  // Access token udløbet → forsøg refresh (men ikke på auth-endpoints selv)
  if (res.status === 401 && !opts.isRetry && !opts.skipRefresh) {
    const refreshToken = getRefresh();
    if (!refreshToken) { clearTokens(); throw new Error('SESSION_EXPIRED'); }

    // Undgå parallelle refresh-kald
    if (!refreshPromise) {
      refreshPromise = fetch(`${getBase()}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).then(async r => {
        refreshPromise = null;
        if (!r.ok) { clearTokens(); throw new Error('SESSION_EXPIRED'); }
        return r.json();
      }).catch(err => { refreshPromise = null; throw err; });
    }

    const tokens = await refreshPromise;
    saveTokens(tokens);
    return req(method, path, body, { ...opts, isRetry: true });
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const error = new Error(err.error || 'Fejl');
    error.code = err.code || null;
    // Gør code tilgængeligt via både .code og i message for robusthed
    if (err.code) error.limitCode = err.code;
    throw error;
  }
  return res.json();
}

export const api = {
  // ── Auth ──────────────────────────────────────────────────────────────
  // skipRefresh:true → token refresh ikke forsøgt på auth-endpoints selv (undgår loops)
  register: (data) => req('POST', '/auth/register', data, { skipHousehold: true, skipRefresh: true }),
  login:    (email, password) => req('POST', '/auth/login', { email, password }, { skipHousehold: true, skipRefresh: true }),
  logout:   async () => {
    try { await req('POST', '/auth/logout', { refresh_token: getRefresh() }, { skipHousehold: true, skipRefresh: true }); } catch {}
    clearTokens();
  },
  me:       () => req('GET', '/auth/me', undefined, { skipHousehold: true }),
  updateMe: (data) => req('PATCH', '/auth/me', data, { skipHousehold: true }),
  deleteAccount: (password) => req('DELETE', '/auth/account', { password }, { skipHousehold: true }),
  changePassword: (current_password, new_password) => req('PATCH', '/auth/me/password', { current_password, new_password }, { skipHousehold: true }),
  forgotPassword: (email) => req('POST', '/auth/forgot-password', { email }, { skipHousehold: true, skipRefresh: true }),
  resetPassword:  (token, new_password) => req('POST', '/auth/reset-password', { token, new_password }, { skipHousehold: true, skipRefresh: true }),

  saveTokens,
  clearTokens,
  getActiveHousehold,
  setActiveHousehold,
  isLoggedIn: () => !!getAccess(),

  // ── Husstande ─────────────────────────────────────────────────────────
  getHouseholds:        () => req('GET', '/households', undefined, { skipHousehold: true }),
  createHousehold:      (data) => req('POST', '/households', data, { skipHousehold: true }),
  joinHousehold:        (invite_code) => req('POST', '/households/join', { invite_code }, { skipHousehold: true }),
  getHouseholdMembers:  (id) => req('GET', `/households/${id}/members`, undefined, { skipHousehold: true }),
  updateHousehold:      (id, data) => req('PATCH', `/households/${id}`, data, { skipHousehold: true }),
  leaveHousehold:       (id, userId) => req('DELETE', `/households/${id}/members/${userId}`, undefined, { skipHousehold: true }),
  removeMember:         (id, userId) => req('DELETE', `/households/${id}/members/${userId}`, undefined, { skipHousehold: true }),
  updateMemberRole:     (id, userId, role) => req('PATCH', `/households/${id}/members/${userId}`, { role }, { skipHousehold: true }),
  regenerateCode:       (id) => req('POST', `/households/${id}/regenerate-code`, undefined, { skipHousehold: true }),

  // ── Katalog ───────────────────────────────────────────────────────────
  getCatalog:     (listType) => req('GET', `/catalog${listType ? `?list_type=${listType}` : ''}`),
  createProduct:  (data) => req('POST', '/catalog', data),
  updateProduct:  (id, data) => req('PUT', `/catalog/${id}`, data),
  deleteProduct:  (id, force = false) => req('DELETE', `/catalog/${id}${force ? '?force=true' : ''}`),

  // ── Inventory ─────────────────────────────────────────────────────────
  getInventory:    (listType) => req('GET', `/inventory?list_type=${listType}`),
  addInventory:    (data) => req('POST', '/inventory', data),
  updateInventory: (id, data) => req('PUT', `/inventory/${id}`, data),
  useInventory:    (id, amount = 1) => req('POST', `/inventory/${id}/use`, { amount }),
  deleteInventory: (id) => req('DELETE', `/inventory/${id}`),

  // ── Shopping ──────────────────────────────────────────────────────────
  getShopping:    () => req('GET', '/shopping'),
  addShopping:    (data) => req('POST', '/shopping', data),
  updateShopping: (id, data) => req('PUT', `/shopping/${id}`, data),
  deleteShopping: (id) => req('DELETE', `/shopping/${id}`),
  clearChecked:   () => req('DELETE', '/shopping/checked'),

  // ── Lokationer ────────────────────────────────────────────────────────
  getLocations:      (listType) => req('GET', `/locations${listType ? `?list_type=${listType}` : ''}`),
  getLocationsByUnit:(unitId) => req('GET', `/locations?storage_unit_id=${unitId}`),
  addLocation:       (data) => req('POST', '/locations', data),
  updateLocation:    (id, data) => req('PUT', `/locations/${id}`, data),
  deleteLocation:    (id) => req('DELETE', `/locations/${id}`),

  // ── Lagerenheder ──────────────────────────────────────────────────────
  getStorageUnits:   (listType) => req('GET', `/storage-units${listType ? `?list_type=${listType}` : ''}`),
  createStorageUnit: (data) => req('POST', '/storage-units', data),
  updateStorageUnit: (id, data) => req('PATCH', `/storage-units/${id}`, data),
  deleteStorageUnit: (id, force) => req('DELETE', `/storage-units/${id}${force ? '?force=true' : ''}`),

  // ── Barcode ───────────────────────────────────────────────────────────
  lookupBarcode: (code) => req('GET', `/barcode/${code}`),

  // ── Push ──────────────────────────────────────────────────────────────
  getVapidKey:       () => req('GET', '/push/vapid-key'),
  subscribePush:     (sub, userLabel) => req('POST', '/push/subscribe', { ...sub, userLabel }),
  unsubscribePush:   (endpoint) => req('DELETE', '/push/subscribe', { endpoint }),
  registerPushToken: (token, platform) => req('POST', '/push/register-token', { token, platform }),
  unregisterPushToken: (token) => req('DELETE', '/push/register-token', { token }),

  // ── Upload ────────────────────────────────────────────────────────────
  uploadImage: async (productId, file, label) => {
    const fd = new FormData();
    fd.append('image', file);
    if (label) fd.append('label', label);
    const headers = {};
    const token = getAccess();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const hhId = getActiveHousehold();
    if (hhId) headers['X-Household-Id'] = hhId;
    const res = await fetch(`${getBase()}/api/upload/image/${productId}`, { method: 'POST', headers, body: fd });
    if (!res.ok) throw new Error('Upload fejlede');
    return res.json();
  },
  deleteImage: (imageId) => req('DELETE', `/upload/image/${imageId}`),

  // ── Forbrug / historik ───────────────────────────────────────────────
  getConsumption: (limit) => req('GET', `/consumption${limit ? `?limit=${limit}` : ''}`),
  getConsumptionStats: () => req('GET', '/consumption/stats'),

  // ── Premium ───────────────────────────────────────────────────────────
  getPremiumStatus:  () => req('GET', '/premium/status', undefined, { skipHousehold: true }),
  validateReceipt:   (body) => req('POST', '/premium/validate', body, { skipHousehold: true }),
  devActivatePremium:() => req('POST', '/premium/dev-activate', undefined, { skipHousehold: true }),
  deactivatePremium: () => req('DELETE', '/premium/deactivate', undefined, { skipHousehold: true }),

  // ── Kostvane-præferencer ──────────────────────────────────────────────
  getPreferences:    () => req('GET', '/preferences', undefined, { skipHousehold: true }),
  updatePreferences: (filters) => req('PUT', '/preferences', { filters }, { skipHousehold: true }),

  // ── Personal Access Tokens (til smarthome-integrationer) ─────────────
  listTokens:   () => req('GET', '/tokens', undefined, { skipHousehold: true }),
  createToken:  (name) => req('POST', '/tokens', { name }, { skipHousehold: true }),
  deleteToken:  (id) => req('DELETE', `/tokens/${id}`, undefined, { skipHousehold: true }),
};
